// import Sidebar from '../components/shared/Sidebar';
// import Header from '../components/shared/Header';
// import Footer from '../components/shared/Footer';

// export default function DeveloperLayout({ children }) {
//   const handleLogout = () => {
//     // Clear session storage/local storage
//     sessionStorage.clear();
//     localStorage.clear();
//     // Optionally redirect to login page
//     window.location.href = '/login';
//   };

//   return (
//     <div className="flex min-h-screen bg-gray-100">
//       <Sidebar role="developer" />
//       <div className="flex flex-col flex-1">
//         <Header onLogout={handleLogout} />
//         <main className="flex-1 p-6">{children}</main>
//         <Footer />
//       </div>
//     </div>
//   );
// }

// In DeveloperLayout.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Sidebar from '../components/shared/Sidebar';
import Header from '../components/shared/Header';
import Footer from '../components/shared/Footer';
import { useCompanyPermissions } from '../hooks/useCompanyPermissions';
import { developerMenuItems, companyOnlyMenuLabels, findMenuForPath } from '../constants/developerMenu';
import { useCompany } from '../contexts/CompanyContext';

export default function DeveloperLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { activeCompanyId } = useCompany();
  const { allowedMenus, hasCompany, loading: permissionsLoading } = useCompanyPermissions(true, activeCompanyId);

  // "Users" / "Invite Users" only make sense for an account linked to a
  // company. Every professional role that never got a company_id (dentist,
  // hygienist, dental_professional) must have those hidden - but an invited
  // teammate's User.role is literally "owner"/"sub_user" (see accept-invite),
  // not "developer_software_company", so company membership (hasCompany)
  // is the only reliable signal here, not the role string.
  const effectiveAllowedMenus = hasCompany
    ? allowedMenus
    : new Set(
        (allowedMenus ? developerMenuItems.filter((item) => allowedMenus.has(item.label)) : developerMenuItems)
          .map((item) => item.label)
          .filter((label) => !companyOnlyMenuLabels.has(label))
      );

  // Close sidebar when route changes (on mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    // Clear all auth data
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    sessionStorage.clear();
    // Navigate to login
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // A user who lands on (or is linked to) a page outside their allowed
  // menus - whether restricted by sub_user permissions or by not having a
  // company at all - sees a "No Access" message in place of the page,
  // instead of being silently redirected away.
  let accessDenied = false;
  if (effectiveAllowedMenus) {
    const menu = findMenuForPath(location.pathname, location.search);
    accessDenied = Boolean(menu && !effectiveAllowedMenus.has(menu.label));
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - hidden on mobile by default, shown when toggled */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out
        lg:sticky lg:top-0 lg:translate-x-0 lg:z-auto lg:h-screen
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar
          role="developer"
          onLogout={handleLogout}
          onClose={() => setSidebarOpen(false)}
          allowedMenus={effectiveAllowedMenus}
        />
      </div>
      
      <div className="flex flex-col flex-1 min-w-0">
        <Header onLogout={handleLogout} onMenuToggle={toggleSidebar} />
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-x-hidden">
          {!accessDenied && children}
        </main>
        <Footer />
      </div>

      {accessDenied && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto">
              <Lock className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Restricted</h2>
            <p className="text-gray-500 mb-6">
              You don't have permission to access this section. Please contact your account owner to request access.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}